import { CustomFetchApiError } from '../errors';
import { getErrorCustomData } from '../errors/CustomFetchApiError';
import { CustomErrorTypes } from './types';
import { Logger } from 'Common/appshell_types/loggerTypes';

const buildErrorObject = (message: string, url: string, body: object = {}): CustomFetchApiError => {
    const error = new CustomFetchApiError(message);
    error.url = url;
    error.body = body;
    return error;
};

/**
 * Handles default errors from the api response
 * @param url
 * @param body
 * @param response
 */
export const defaultErrorHandler = (url: string, body: object = {}, response: Response): CustomFetchApiError | void => {
    if (response.status !== 400) {
        const error = buildErrorObject(response.statusText, url, body);
        error.code = response.status;
        throw error;
    }
};

/**
 * A higher function that takes a custom message to be returned if the status code is 404
 * @param message
 */
export const custom404ErrorHandler = (message: string): any => {
    return (url: string, body: object = {}, response: Response): CustomFetchApiError => {
        const text = response.status === 404 ? message : response.statusText;
        const error = buildErrorObject(text, url, body);
        error.code = response.status;
        throw error;
    };
};

/**
 * A higher function that takes a list of status codes to ignore and then uses the the default error handler
 * @param status
 */
export const exemptStatusErrorHandler = (status: Array<number>): any => {
    return (url: string, body: object = {}, response: Response): CustomFetchApiError | void => {
        if (!status.includes(response.status)) {
            defaultErrorHandler(url, body, response);
        }
    };
};

/**
 * This checks if the type of error is the "Failed to fetch" from the fetch http library we use, and build a custom
 * error object if it is.
 * @param error
 * @param url
 * @param body
 */
export const parseException = (error: Error, url: string, body: object = {}): Error => {
    if (error.name === CustomErrorTypes.CONNECTIVITY_ERROR) {
        const connectivityError = new CustomFetchApiError(`Could not make request to the url: ${url}`);
        connectivityError.url = url;
        connectivityError.body = body;
        return connectivityError;
    }
    return error;
};

/**
 * Check log 404 errors as warnings instead
 * @param err
 * @param identifier
 * @param logger
 * @param action
 */
// eslint-disable-next-line max-params
export const log404ErrorAsWarning = (err: Error, identifier: string, logger: Logger, action: string): void => {
    const customData = {
        action,
        ...getErrorCustomData(err),
        payload: { identifier },
    };
    if (CustomFetchApiError.isCustomError(err) && err.code === 404) {
        logger.warn(err.message, customData);
        return;
    }
    logger.error(err.message, customData);
};

export type ErrorHandlerFunction = (...args: Parameters<typeof defaultErrorHandler>) => CustomFetchApiError | void;
